import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { SvgIcon } from '@mui/material';
import { Footer } from '../components';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/bio', label: 'Bio' },
  { to: '/concerts', label: 'Concerts' },
  { to: '/contact', label: 'Contact' },
];

const MenuIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="currentColor" />
  </SvgIcon>
);

const CloseIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
  </SvgIcon>
);

/**
 * Main layout: Navbar, content area, footer.
 * Navbar: simple, clean, modern (constrained width, text links, mobile drawer).
 */
export function MainLayout({ children, title, maxWidth = 'lg', showFooter = true }) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navContent = (
    <>
      {NAV_LINKS.map(({ to, label }) => {
        const active = location.pathname === to;
        return (
          <Typography
            key={to}
            component={RouterLink}
            to={to}
            variant="body1"
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              fontWeight: active ? 600 : 400,
              letterSpacing: '0.02em',
              px: 1.5,
              py: 0.5,
              borderBottom: active ? '2px solid currentColor' : '2px solid transparent',
              borderRadius: 0,
              transition: theme.transitions.create(['border-color', 'opacity', 'font-weight']),
              opacity: active ? 1 : 0.88,
              '&:hover': {
                opacity: 1,
                borderBottomColor: 'currentColor',
              },
            }}
          >
            {label}
          </Typography>
        );
      })}
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'primary.main',
          borderBottom: '1px solid',
          borderColor: 'rgba(255,255,255,0.08)',
        }}
      >
        <Container maxWidth={maxWidth} disableGutters={false}>
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 56, sm: 64 },
              justifyContent: 'space-between',
              px: { xs: 0, sm: 0 },
            }}
          >
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                '&:hover': { opacity: 0.92 },
                transition: 'opacity 0.2s ease',
              }}
            >
              {title}
            </Typography>

            {isMobile ? (
              <>
                <IconButton
                  color="inherit"
                  aria-label="Menu"
                  onClick={() => setDrawerOpen(true)}
                  sx={{ ml: 'auto' }}
                >
                  <MenuIcon />
                </IconButton>
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                  PaperProps={{
                    sx: {
                      width: 280,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    },
                  }}
                >
                  <Box sx={{ py: 2, px: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton color="inherit" onClick={() => setDrawerOpen(false)} aria-label="Fermer">
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <List sx={{ px: 2 }}>
                    {NAV_LINKS.map(({ to, label }) => (
                      <ListItem key={to} disablePadding>
                        <ListItemButton
                          component={RouterLink}
                          to={to}
                          onClick={() => setDrawerOpen(false)}
                          selected={location.pathname === to}
                          sx={{
                            borderRadius: 1,
                            '&.Mui-selected': {
                              bgcolor: 'rgba(255,255,255,0.12)',
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                            },
                          }}
                        >
                          <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 500 }} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Drawer>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{navContent}</Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="main" sx={{ flex: 1 }}>
        {children}
      </Box>

      {showFooter && <Footer siteName={title} />}
    </Box>
  );
}

export default MainLayout;
